import PurchaseHistoryDetailPageView from 'src/pages/PurchaseHistoryPage/PurchaseHistoryDetailPage/view'
import { QueryClient } from '@tanstack/react-query'
import {
  cancelCouponValidQueryOption,
  couponDetailInfoTabQueryOption,
  purchaseHistoryDetailQueryOption,
  useCustomQuery,
  walletCouponUsedCheckQueryOption,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CouponCancelValidSpace,
  CouponDetailInfoTabSpace,
  PurchaseCancelSpace,
  PurchaseHistoryDetailSpace,
  PurchaseValidCheckSpace,
} from 'src/types/api'
import { handleJsonParse, maskPhoneNumber } from 'src/utils'
import { DateUtils } from 'src/utils/date'
import purchase_history_check from 'src/assets/images/icon/icon_check.png'
import purchase_history_clock from 'src/assets/images/icon/icon_clock.png'

import { useModal } from 'src/provider/ModalContextProvider'
import {
  purchaseCancelQueryOption,
  purchaseValidCheckQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import { Params } from 'react-router-dom'
import React, { useCallback, useEffect, useState } from 'react'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_PURCHASE_CANCEL,
  ERRCODES_PURCHASE_VALID_CHECK,
} from 'src/service/constant/errcodes'
import usePageNav from 'src/hooks/usePageNav.hook'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import dayjs from 'dayjs'

export interface PurchaseHistoryDetailPageProps {
  purchaseHistoryDetailData?: PurchaseHistoryDetailSpace.Response
  couponStatus: string
  isAlwaysDcCoupon: boolean
  isUseAble: boolean
  isUseComplete: boolean
  barcodeDeemItem: { text: string; imgSrc: string }
  purchaseHistoryDetailDataList: ({ text: string; value: string } | null)[]
  addPurchaseCouponHandler: () => void
  cancelPurchaseCouponHandler: () => void
  giftCouponHandler: () => void
  couponDetailTabData?: CouponDetailInfoTabSpace.Response
  Introduction?: string
  isCancelAble?: boolean
  isShowBarcode: boolean
  showBarcodeHandler: () => void
}

const barcodeDeemItems: { [key: string]: { text: string; imgSrc: string } } = {
  '1': { text: '사용 완료', imgSrc: purchase_history_check },
  '2': { text: '취소 완료', imgSrc: purchase_history_check },
  '3': { text: '취소 완료', imgSrc: purchase_history_check },
  '4': { text: '기간 만료', imgSrc: purchase_history_clock },
}

export const purchaseHistoryDetailPageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { idx, couponType, couponNumber } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(
        couponDetailInfoTabQueryOption({
          CouponType: Number(couponType!) as 0 | 1,
          CouponNumber: couponNumber!,
        }),
      ),
      await queryClient.ensureQueryData(purchaseHistoryDetailQueryOption({ Idx: Number(idx!) })),
      await queryClient.ensureQueryData(cancelCouponValidQueryOption({ Idx: Number(idx!) })),
    ])
  }

const PurchaseHistoryDetailPage = () => {
  const { couponIdx, couponNumber, couponStatus, couponType, idx } = useParamsType()
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { DateFormat, getAfter7Days, now } = DateUtils
  const isUseAble = couponStatus === '0'
  const isUseComplete = couponStatus === '1'
  const { c_tel } = useCustomCookies()
  const {
    purchasePageNav,
    qnaPageNav,
    refundPageNav,
    couponPurchaseCancelCompletePageNav,
    couponDetailPageNav,
    navigatePrev,
    giftSendPurchasedPageNav,
    purchaseHistoryDetailPageNav,
  } = usePageNav()
  const [isShowBarcode, setIsShowBarcode] = useState(false)

  const { data: purchaseHistoryDetailData, isLoading: isPurchaseHistoryDetailLoading } =
    useCustomQuery<PurchaseHistoryDetailSpace.Response>(
      purchaseHistoryDetailQueryOption({ Idx: Number(idx) }),
    )

  const { data: cancelCouponValidQueryData, isLoading: isCancelCouponValidLoading } =
    useCustomQuery<CouponCancelValidSpace.Response>(
      cancelCouponValidQueryOption({ Idx: Number(idx) }),
    )

  const { data: walletCouponCheckQueryData, isLoading: isWalletCouponUsedCheckLoading } =
    useCustomQuery(
      walletCouponUsedCheckQueryOption(
        { CouponOrderID: `${purchaseHistoryDetailData?.Body?.CouponOrderID}` },
        isUseAble && !!purchaseHistoryDetailData?.Body?.CouponOrderID,
      ),
    )

  const {
    data: couponDetailInfoTabQueryData,
    isSuccess: isCouponDetailInfoSuccess,
    isLoading: isCouponDetailInfoLoading,
  } = useCustomQuery<CouponDetailInfoTabSpace.Response>(
    couponDetailInfoTabQueryOption({
      CouponType: Number(couponType) as 0 | 1,
      CouponNumber: couponNumber,
    }),
  )

  const { mutateAsync: purchaseCancelMutate, isPending: isPurchaseCancelPending } =
    useCustomMutation<PurchaseCancelSpace.Request, PurchaseCancelSpace.Response>(
      purchaseCancelQueryOption(),
    )

  const { mutateAsync: purchaseValidCheckMutate } = useCustomMutation<
    PurchaseValidCheckSpace.Request,
    PurchaseValidCheckSpace.Response
  >(purchaseValidCheckQueryOption())

  const {
    CouponType,
    CouponOrderID,
    RegDT,
    SalePrice,
    ExpireDT,
    ValidDay,
    RegularPrice = 0,
    Idx,
    PinNumber,
  } = purchaseHistoryDetailData?.Body as PurchaseHistoryDetailSpace.Response

  const setCouponDetailInfoItems = useCallback(() => {
    return {
      ...couponDetailInfoTabQueryData?.Body,
      CouponInfo: {
        Title: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Title,
        Contents: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Contents?.replace(
          '{{ValidDay}}',
          `${ValidDay}일`,
        ),
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponDetailInfoSuccess, couponDetailInfoTabQueryData])

  const isAlwaysDcCoupon = CouponType === 0
  const isCancelAble = cancelCouponValidQueryData?.Body?.CancelBtnFlag === 1

  const purchaseHistoryDetailDataList = [
    ...(isUseAble ? [{ text: '유효기간', value: DateFormat(ExpireDT, 'YY.MM.DD') }] : []),
    ...(PinNumber?.trim() ? [{ text: '쿠폰인증번호', value: PinNumber.trim() }] : []),
    { text: '주문번호', value: CouponOrderID ? CouponOrderID : '' },
    { text: '구매 날짜', value: DateFormat(RegDT, 'YY.MM.DD') },
    ...(RegularPrice ? [{ text: '상품 금액', value: `${RegularPrice.toLocaleString()}원` }] : []),
    { text: '결제 금액', value: SalePrice ? `${SalePrice?.toLocaleString()}원` : `` },
    { text: '받는 사람', value: maskPhoneNumber(c_tel) },
  ]

  const barcodeDeemItem = barcodeDeemItems[couponStatus]

  const purchaseCancelSubmit = () => {
    purchaseCancelMutate({ Idx: Number(idx) }).then(({ Body, Header }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        couponPurchaseCancelCompletePageNav(Number(idx), couponNumber, CouponOrderID)
      } else {
        const ErrMsg = handleJsonParse(Header.ErrMsg)
        const { Title = '', Contents = '' } = ErrMsg
        const handleRefundPageNav = () =>
          refundPageNav(Number(idx), Number(couponIdx), couponNumber, CouponOrderID, RegDT)

        switch (Header.ErrCode) {
          case ERRCODES_PURCHASE_CANCEL.ERROR_ALREADY_USE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { cancel: { t: '닫기' } },
            })
            break
          case ERRCODES_PURCHASE_CANCEL.ERROR_CANCEL_PERIOD_OVER:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '환불 신청', f: handleRefundPageNav }, cancel: { t: '닫기' } },
            })
            break
          case ERRCODES_PURCHASE_CANCEL.ERROR_CANCEL_ERROR:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '문의하기', f: qnaPageNav }, cancel: { t: '닫기' } },
            })
            break
          //통신 모듈에 있는 에러 핸들링 사용
          case ERRCODES_COMMON.PIN_CHECK_ERROR:
          case ERRCODES_COMMON.SESSION_ERROR:
          case ERRCODES_COMMON.ERROR_LOGOUT:
            break
          default:
            openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
            break
        }
      }
    })
  }

  const addPurchaseCouponHandler = () => {
    const submitData = {
      CouponIdx: Number(couponIdx),
      CouponType: CouponType as 0 | 1 | 2,
      SalePrice: SalePrice as number,
    }

    purchaseValidCheckMutate(submitData).then(({ Header, Body }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        purchasePageNav(Number(couponType), Number(couponIdx), couponNumber)
      } else {
        const ErrMsg = handleJsonParse(Header.ErrMsg)
        const { Title = '', Contents = '' } = ErrMsg
        const handleCouponDetailPageNav = () =>
          couponDetailPageNav(couponNumber, Number(couponIdx), Number(couponType) as 0 | 1 | 2)

        switch (Header.ErrCode) {
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_NOT_VALID_COUPON:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_HOT_DEAL_SOLD_OUT:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_ONLY_ONE_HOTDEAL_PURCHASE:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DAILY_PURCHASED:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '확인' } },
            })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DIFFERENT_PRICE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '확인', f: handleCouponDetailPageNav } },
            })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_MAX_OF_MONTH_HOTDEAL_PURCHASE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '닫기' } },
            })
            break
          //통신 모듈에 있는 에러 핸들링 사용
          case ERRCODES_COMMON.SESSION_ERROR:
            break
          default:
            openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
            break
        }
      }
    })
  }

  const cancelPurchaseCouponHandler = () => {
    openModal({
      contents: { t: '구매취소', d: '구매를 취소하시겠습니까?' },
      button: { submit: { t: '확인', f: purchaseCancelSubmit }, cancel: { t: '취소' } },
    })
  }

  const showBarcodeHandler = () => {
    if (isUseComplete && !isShowBarcode) setIsShowBarcode(true)
  }

  const giftCouponHandler = () => {
    const after7Days = getAfter7Days(now()).endOf('day')
    if (after7Days >= dayjs(ExpireDT)) {
      openModal({
        contents: {
          t: '선물하기 이용 안내',
          d: '쿠폰 유효기간이 7일 이하로 남아 선물하기를 이용할 수 없습니다.',
        },
        button: { submit: { t: '확인' } },
      })
    } else {
      giftSendPurchasedPageNav(Idx, couponStatus, couponNumber)
    }
  }

  useEffect(() => {
    if (isUseAble && walletCouponCheckQueryData?.Body?.Use === 'Y') {
      navigatePrev()
      openToast({ contents: '이미 사용한 쿠폰입니다.' })
    } else if (walletCouponCheckQueryData?.Body?.Use === 'C') {
      purchaseHistoryDetailPageNav(
        Idx,
        Number(couponIdx),
        '2',
        Number(couponType),
        couponNumber,
        true,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletCouponCheckQueryData])

  const props: PurchaseHistoryDetailPageProps = {
    purchaseHistoryDetailData: purchaseHistoryDetailData?.Body,
    couponDetailTabData: setCouponDetailInfoItems(),
    couponStatus,
    Introduction: purchaseHistoryDetailData?.Body?.Introduction,
    isAlwaysDcCoupon,
    isUseAble,
    isUseComplete,
    addPurchaseCouponHandler,
    cancelPurchaseCouponHandler,
    giftCouponHandler,
    barcodeDeemItem,
    purchaseHistoryDetailDataList,
    isCancelAble,
    isShowBarcode,
    showBarcodeHandler,
  }

  const renderLoading =
    isCouponDetailInfoLoading ||
    isPurchaseHistoryDetailLoading ||
    isCancelCouponValidLoading ||
    isPurchaseCancelPending ||
    isWalletCouponUsedCheckLoading

  return (
    <React.Fragment>
      <RenderIf when={renderLoading}>
        <Loading.Default />
      </RenderIf>
      <PurchaseHistoryDetailPageView {...props} />
    </React.Fragment>
  )
}
export default PurchaseHistoryDetailPage
